import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { Container, Sprite, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { getUITexture } from "../../../../assets";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

const PopupButton = ({
  x,
  y,
  text,
  onClick,
  active = false,
  disabled = false,
  width,
  height,
  fontWeight = "500",
  accessibleTitle,
}) => {
  const containerRef = useRef(null);
  const { hoverContainerRef, buttonRef, handlePointerOver: hoverIn, handlePointerOut: hoverOut, triggerPress } = useButtonHover();

  // Textures — popup_button for default, popup_button_active for hovered/active
  const defaultTexture = getUITexture("popup_button");
  const activeTexture = getUITexture("popup_button_active");
  const infinityTexture = getUITexture("icon_infinity");

  const isInfinityButton = text === "∞";

  const infinityIconSize = useMemo(() => {
    if (!isInfinityButton || !infinityTexture) return { width: 0, height: 0 };
    const iconAspectRatio = infinityTexture.width / infinityTexture.height;
    const maxWidth = width * 0.7;
    const maxHeight = height * 0.9;
    let iconWidth = maxWidth;
    let iconHeight = iconWidth / iconAspectRatio;
    if (iconHeight > maxHeight) {
      iconHeight = maxHeight;
      iconWidth = iconHeight * iconAspectRatio;
    }
    return { width: iconWidth, height: iconHeight };
  }, [isInfinityButton, infinityTexture, width, height]);

  // Auto-scaling font size
  const finalFontSize = useMemo(() => {
    const idealFontSize = Math.round(height * 0.5);
    const maxTextWidth = width * 0.9;
    const tempStyle = new PIXI.TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: fontWeight,
    });
    const metrics = PIXI.TextMetrics.measureText(text, tempStyle);
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }
    return idealFontSize;
  }, [text, width, height, fontWeight]);

  const textStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalFontSize,
        fontWeight: fontWeight,
        fill: disabled ? 0x666666 : (active ? 0x090f41 : 0xffffff),
        align: "center",
      }),
    [finalFontSize, disabled, active, fontWeight]
  );

  const handleClick = useCallback(() => {
    if (disabled) return;
    soundManager.play(SOUND_NAME.button_click);
    triggerPress();
    onClick();
  }, [disabled, onClick, triggerPress]);

  // Apply active texture when selected
  useEffect(() => {
    if (buttonRef.current && !disabled) {
      buttonRef.current.texture = active ? activeTexture : defaultTexture;
    }
  }, [active, disabled, activeTexture, defaultTexture, buttonRef]);

  // Hover: swap to active texture + scale
  const handlePointerOver = useCallback(() => {
    if (disabled) return;
    if (buttonRef.current && activeTexture) {
      buttonRef.current.texture = activeTexture;
    }
    hoverIn();
  }, [disabled, activeTexture, buttonRef, hoverIn]);

  const handlePointerOut = useCallback(() => {
    if (buttonRef.current && defaultTexture) {
      // Only revert if not the active/selected button
      if (!active) {
        buttonRef.current.texture = defaultTexture;
      }
    }
    hoverOut();
  }, [active, defaultTexture, buttonRef, hoverOut]);

  return (
    <Container ref={containerRef} name="backport_bet_button" x={x} y={y}>
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={active ? activeTexture : defaultTexture}
          name="backport_bet_button_background"
          anchor={0.5}
          width={width}
          height={height}
          alpha={disabled ? 0.5 : 1}
          eventMode={disabled ? "none" : "static"}
          cursor={disabled ? "default" : "pointer"}
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={!!accessibleTitle}
          accessibleTitle={accessibleTitle}
        />

        {isInfinityButton && infinityTexture ? (
          <Sprite
            texture={infinityTexture}
            anchor={0.5}
            width={infinityIconSize.width}
            height={infinityIconSize.height}
            x={0}
            y={0}
            alpha={disabled ? 0.5 : 1}
            eventMode="none"
          />
        ) : (
          <Text
            text={text}
            style={textStyle}
            anchor={0.5}
            x={0}
            y={0}
            resolution={2}
            eventMode="none"
          />
        )}
      </Container>
    </Container>
  );
};

export default PopupButton;
